import ExportButton from '../../components/ExportButton';
import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { LuPencil, LuCheck, LuX, LuCalendar, LuClock, LuChevronDown } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomModal from "../../components/CustomModal";
import Input from "../../components/input";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import NewTableComponent from "../../components/NewTableComponent";
import Select from "../../components/Select";
import Stats from "../../components/Stats";
import SupervisorFilterDropdown from "../../components/SupervisorFilterDropdown";
import UserAvatar from "../../components/UserAvatar";
import {
  deleteDriver,
  fetchDriverOverview,
  fetchDriversPaginated,
  updateDriverStatus,
} from "../../store/Slices/DriverSlice";
import { fetchSupervisorsDropdown } from "../../store/Slices/dropdownSlice";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/Services.css";
import "../../styles/Table.css";
import {
  AppMainModules,
  DriverSafetyPermission,
  OperationsSubModules,
} from "../../utils/app-constant";
import { hasPermission } from "../../utils/helper";
import { errorNotify, successNotify } from "../../utils/toast";
import { apiService } from "../../services/apiService";
import { useDriverListingExport } from "./useDriverListingExport";
import {
  lowStockIcon,
  partUtilizedIcon,
  partValueIcon,
  plusIcon,
  resolvedIssueIcon,
  successIcon,
  warningIcon,
  wrongIcon,
} from "../../utils/svgs";

const STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "On Leave", value: "On Leave" },
  { label: "Inactive", value: "Inactive" },
];

const STATUS_UPDATE_OPTIONS = [
  { label: "Active", value: "active", icon: successIcon },
  { label: "Inactive", value: "inactive", icon: wrongIcon },
];

const ATTENDANCE_STATUS_OPTIONS = [
  {
    label: (
      <div className="d-flex align-items-center gap-2">
        <LuCheck style={{ fontSize: "14px" }} /> Present
      </div>
    ),
    value: "Present",
  },
  {
    label: (
      <div className="d-flex align-items-center gap-2">
        <LuX style={{ fontSize: "14px" }} /> Absent
      </div>
    ),
    value: "Absent",
  },
  {
    label: (
      <div className="d-flex align-items-center gap-2">
        <LuCalendar style={{ fontSize: "14px" }} /> Leave
      </div>
    ),
    value: "Leave",
  },
];

const ATTENDANCE_STATUS_OPTIONS_NO_ICON = [
  { label: "Present", value: "Present" },
  { label: "Absent", value: "Absent" },
  { label: "Leave", value: "Leave" },
];

const DEFAULT_STATUS_POPUP_STATE = {
  open: false,
  rowId: null,
  placement: "bottom",
  triggerRect: null,
};

const DRIVER_STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  on_leave: "On Leave",
  onleave: "On Leave",
};

const normalizeStatus = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (DRIVER_STATUS_LABELS[normalizedValue]) {
    return DRIVER_STATUS_LABELS[normalizedValue];
  }

  if (!normalizedValue) return "Inactive";

  return normalizedValue
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
};

const formatLastActivity = (value) => {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) return "-";

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue;

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getNumberMetric = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getMetricOrFallback = (value, fallback) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getTodayInputDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getAttendanceStatusStyles = (status) => {
  if (status === "Present") {
    return {
      background: "#ecfdf5",
      color: "#047857",
      border: "1px solid #10b981"
    };
  }
  if (status === "Absent") {
    return {
      background: "#fff5f5",
      color: "#c53030",
      border: "1px solid #feb2b2"
    };
  }
  if (status === "Leave") {
    return {
      background: "#fffbeb",
      color: "#b76e37ff",
      border: "1px solid #ffde8aff"
    };
  }
  return {
    background: "transparent",
    color: "#334155",
    border: "1px solid #e2e8f0"
  };
};

const normalizeDriverRecord = (driverRecord = {}) => {
  const user = driverRecord?.user || {};
  const updatedAt = driverRecord?.updated_at ?? "";

  return {
    id: driverRecord?.id ?? "",
    userId: driverRecord?.user_id ?? user?.id ?? "",
    driverId: String(driverRecord?.driver_info_id ?? "").trim(),
    driverName: String(user?.username ?? "").trim(),
    employeeNumber: String(driverRecord?.employee_id ?? "").trim(),
    contactNumber: String(user?.mobile ?? "").trim(),
    supervisorName: String(
      driverRecord?.supervisor?.username ||
        driverRecord?.supervisor?.name ||
        driverRecord?.supervisor_name ||
        ""
    ).trim(),
    status: normalizeStatus(driverRecord?.status),
    safetyScore: Number(driverRecord?.safety_score ?? 0),
    updatedAt,
    lastActivity: formatLastActivity(updatedAt),
    assignedTrips: Number(
      driverRecord?.assign_trips ?? driverRecord?.assigned_trips ?? 0
    ),
    image: String(user?.image ?? "").trim(),
  };
};

const normalizeDriverRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map(normalizeDriverRecord);

const getStatusMeta = (status) => {
  if (status === "Active") {
    return {
      background: "#E8F5E1",
      color: "#299900",
      icon: successIcon,
    };
  }

  if (status === "On Leave") {
    return {
      background: "#EDE7FF",
      color: "#5B38C6",
      icon: warningIcon,
    };
  }

  return {
    background: "#FBE1E1",
    color: "#EF0000",
    icon: wrongIcon,
  };
};

const getScoreMeta = (score) => {
  if (score >= 80) {
    return {
      background: "#E8F8E2",
      color: "#299900",
    };
  }

  if (score >= 60) {
    return {
      background: "#FCEBD7",
      color: "#D47A00",
    };
  }

  return {
    background: "#FBE1E1",
    color: "#E53935",
  };
};

const renderStatusIcon = (icon) => {
  if (!isValidElement(icon)) {
    return icon;
  }

  const normalizedSize = 18;

  return (
    <span
      style={{
        width: "18px",
        height: "18px",
        minWidth: "18px",
        minHeight: "18px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {cloneElement(icon, {
        width: normalizedSize,
        height: normalizedSize,
        style: {
          width: "18px",
          height: "18px",
          minWidth: "18px",
          minHeight: "18px",
          display: "block",
          ...(icon.props?.style || {}),
        },
      })}
    </span>
  );
};

const getStatusUpdateOptionsForRow = (status) => {
  const normalizedCurrentStatus = normalizeStatus(status);

  return STATUS_UPDATE_OPTIONS.filter(
    (option) => normalizeStatus(option.value) !== normalizedCurrentStatus
  );
};

const DriverSafety = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canCreateDriver = hasPermission({
    permissionSlug: DriverSafetyPermission.Create,
    menuSlug: OperationsSubModules.DriverSafety,
    moduleSlug: AppMainModules.operations,
  });
  const canUpdateDriver = hasPermission({
    permissionSlug: DriverSafetyPermission.Update,
    menuSlug: OperationsSubModules.DriverSafety,
    moduleSlug: AppMainModules.operations,
  });
  const canDeleteDriver = hasPermission({
    permissionSlug: DriverSafetyPermission.Delete,
    menuSlug: OperationsSubModules.DriverSafety,
    moduleSlug: AppMainModules.operations,
  });
  const canChangeDriverStatus = hasPermission({
    permissionSlug: DriverSafetyPermission.StatusApproval,
    menuSlug: OperationsSubModules.DriverSafety,
    moduleSlug: AppMainModules.operations,
  });
  const {
    driverOverviewData,
    getDriversPaginatedData,
    getDriversPaginatedLoading,
    deleteDriverLoading,
    updateDriverStatusLoading,
  } = useSelector((state) => state.drivers || {});
  const { supervisorsDropdownData = [] } = useSelector(
    (state) => state.dropdowns || {}
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState(null);
  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useDriverListingExport(searchText, searchFilters);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [statusPopup, setStatusPopup] = useState(DEFAULT_STATUS_POPUP_STATE);
  const [tableLoading, setTableLoading] = useState(false);
  const [noStatusPermissionModal, setNoStatusPermissionModal] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [attendanceDate, setAttendanceDate] = useState(getTodayInputDate());
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceErrors, setAttendanceErrors] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [saveAttendanceLoading, setSaveAttendanceLoading] = useState(false);
  const [attendanceRefreshKey, setAttendanceRefreshKey] = useState(0);
  const [allowanceEntriesMap, setAllowanceEntriesMap] = useState({});
  const [allowancesByDriver, setAllowancesByDriver] = useState({});
  const statusPopupRef = useRef(null);

  useEffect(() => {
    dispatch(fetchDriverOverview());
    dispatch(fetchSupervisorsDropdown());
  }, [dispatch]);

  const supervisorDropdownOptions = useMemo(() => {
    return (supervisorsDropdownData || []).map((supervisor) => ({
      label: supervisor?.label || supervisor?.username || supervisor?.name || String(supervisor?.id ?? supervisor?.value ?? ""),
      value: supervisor?.value ?? supervisor?.id,
    }));
  }, [supervisorsDropdownData]);

  useEffect(() => {
    const payload = {
      page: currentPage,
      limit,
    };

    if (searchText) {
      payload.search_text = searchText;
    }

    if (searchFilters) {
      payload.searchFilters = searchFilters;
    }

    dispatch(fetchDriversPaginated(payload));
  }, [currentPage, dispatch, limit, searchFilters, searchText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusPopupRef.current &&
        !statusPopupRef.current.contains(event.target)
      ) {
        setStatusPopup(DEFAULT_STATUS_POPUP_STATE);
      }
    };

    const handleScroll = () => setStatusPopup(DEFAULT_STATUS_POPUP_STATE);

    if (statusPopup.open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [statusPopup.open]);

  const driverRows = useMemo(
    () => normalizeDriverRows(getDriversPaginatedData?.results || []),
    [getDriversPaginatedData]
  );

  const handleOpenDriverDetails = (row) => {
    navigate(`/driver-safety/details/${row.id}`, {
      state: { driver: row },
    });
  };

  const buildAttendanceRows = () =>
    driverRows.map((driver) => ({
      id: driver.id,
      driverId: driver.driverId || "-",
      driverName: driver.driverName || "-",
      employeeNumber: driver.employeeNumber || "-",
      contactNumber: driver.contactNumber || "-",
      image: driver.image || "",
      attendanceStatus: "Present",
      leaveReason: "",
    }));

  const openAttendanceModal = () => {
    setAttendanceDate(getTodayInputDate());
    setAttendanceRows([]);
    setAttendanceErrors({});
    setAttendanceModalOpen(true);
  };

  const closeAttendanceModal = () => {
    setAttendanceModalOpen(false);
    setAttendanceErrors({});
  };

  useEffect(() => {
    if (!attendanceModalOpen) return;

    let isMounted = true;
    const fetchAttendance = async () => {
      setAttendanceLoading(true);
      try {
        const response = await apiService.getAllAttendance(attendanceDate);
        if (!isMounted) return;
        const rawDrivers = response?.data || [];
        const rows = rawDrivers.map((driver) => {
          const rawLog = driver.attendance_logs;
          const existingLog = Array.isArray(rawLog)
            ? (rawLog.length > 0 ? rawLog[0] : null)
            : (rawLog || null);

          let attendanceStatus = existingLog?.attendance_status || "Present";
          if (attendanceStatus === "OnLeave" || attendanceStatus === "On Leave" || attendanceStatus === "on_leave") {
            attendanceStatus = "Leave";
          }

          const savedLeaveReason = existingLog?.leave_reason || "";

          return {
            id: driver.id,
            user_id: driver.user_id || driver.user?.id || driver.id,
            driverId: driver.driver_info_id || `DRV-${driver.id}`,
            driverName: driver.user?.username || driver.user?.name || "Driver",
            employeeNumber: driver.employee_id || "—",
            contactNumber: driver.user?.mobile || "—",
            image: driver.user?.image || "",
            attendanceStatus,
            leaveReason: savedLeaveReason,
            isAlreadyMarked: existingLog !== null,
            savedAttendanceStatus: attendanceStatus,
            savedLeaveReason,
          };
        });
        setAttendanceRows(rows);

        // Fetch allowances registered for each driver (daily/occasionally active on this date)
        const allowancesResult = {};
        const entriesResult = {};
        await Promise.all(
          rawDrivers.map(async (driver) => {
            const driverInfoId = driver.id;
            try {
              const res = await apiService.getDriverActiveAllowancesForDate(driverInfoId, attendanceDate || getTodayInputDate());
              const activeAllowances = res?.data || [];
              console.log('[Allowances] driver id:', driverInfoId, 'result:', activeAllowances);
              allowancesResult[driverInfoId] = activeAllowances;
              const entriesMap = {};
              activeAllowances.forEach((a) => {
                entriesMap[a.id] = {
                  allowance_id: a.id,
                  is_marked: a.is_marked || false,
                  amount: a.entry_amount ?? a.amount,
                };
              });
              entriesResult[driverInfoId] = entriesMap;
            } catch (err) { console.error('[Allowances] driver id:', driverInfoId, 'error:', err); }
          })
        );
        if (isMounted) {
          setAllowancesByDriver(allowancesResult);
          setAllowanceEntriesMap(entriesResult);
        }
      } catch (error) {
        if (!isMounted) return;
        errorNotify(error || "Failed to load attendance list");
      } finally {
        if (isMounted) {
          setAttendanceLoading(false);
        }
      }
    };

    fetchAttendance();

    return () => {
      isMounted = false;
    };
  }, [attendanceModalOpen, attendanceDate, attendanceRefreshKey]);

  const updateAttendanceRow = (rowId, key, value) => {
    setAttendanceErrors((previousErrors) => {
      if (!previousErrors[rowId]) return previousErrors;

      const nextErrors = { ...previousErrors };
      delete nextErrors[rowId];
      return nextErrors;
    });

    setAttendanceRows((previousRows) =>
      previousRows.map((row) => {
        if (String(row.id) !== String(rowId)) return row;

        if (key === "attendanceStatus" && value !== "Leave") {
          return {
            ...row,
            attendanceStatus: value,
            leaveReason: "",
          };
        }

        return {
          ...row,
          [key]: value,
        };
      })
    );
  };

  const toggleAllowanceEntry = (driverInfoId, allowanceId, checked) => {
    setAllowanceEntriesMap((prev) => ({
      ...prev,
      [driverInfoId]: {
        ...(prev[driverInfoId] || {}),
        [allowanceId]: {
          ...(prev[driverInfoId]?.[allowanceId] || {}),
          allowance_id: allowanceId,
          is_marked: checked,
          amount: prev[driverInfoId]?.[allowanceId]?.amount ?? (allowancesByDriver[driverInfoId]?.find((a) => a.id === allowanceId)?.amount || 0),
        },
      },
    }));
  };

  const handleUnmarkAttendance = async (row) => {
    try {
      await apiService.unmarkDriverAttendance({
        driver_id: Number(row.user_id),
        attendance_date: attendanceDate,
      });
      successNotify("Attendance unmarked successfully");
      setAttendanceRefreshKey((k) => k + 1);
    } catch (error) {
      errorNotify(error || "Failed to unmark attendance");
    }
  };

  const saveAttendance = async () => {
    if (saveAttendanceLoading) return;

    const nextErrors = attendanceRows.reduce((errors, row) => {
      if (row.attendanceStatus === "Leave" && !String(row.leaveReason || "").trim()) {
        errors[row.id] = "Leave reason is required";
      }

      return errors;
    }, {});

    if (Object.keys(nextErrors).length) {
      setAttendanceErrors(nextErrors);
      return;
    }

    setSaveAttendanceLoading(true);
    try {
      const payload = {
        attendances: attendanceRows.map((row) => {
          let statusVal = row.attendanceStatus;
          if (statusVal === "Leave") {
            statusVal = "OnLeave";
          }
          return {
            driver_id: Number(row.user_id),
            attendance_status: statusVal,
            leave_reason: row.leaveReason || "",
            attendance_date: attendanceDate,
          };
        }),
      };

      await apiService.saveDriverAttendance(payload);

      // Save allowance entries
      const allowanceEntries = [];
      for (const row of attendanceRows) {
        const driverInfoId = row.id;
        const entriesForDriver = allowanceEntriesMap[driverInfoId] || {};
        Object.values(entriesForDriver).forEach((entry) => {
          allowanceEntries.push({
            driver_info_id: driverInfoId,
            allowance_id: entry.allowance_id,
            entry_date: attendanceDate,
            amount: Number(entry.amount || 0),
            is_marked: Boolean(entry.is_marked),
          });
        });
      }
      if (allowanceEntries.length > 0) {
        await apiService.saveAllowanceEntries(allowanceEntries);
      }

      successNotify("Attendance & allowances saved successfully");
      setAttendanceRefreshKey((k) => k + 1);
    } catch (error) {
      errorNotify(error || "Failed to save attendance");
    } finally {
      setSaveAttendanceLoading(false);
    }
  };

  const selectedDeleteRow = useMemo(
    () => driverRows.find((item) => String(item?.id || "") === String(deleteId || "")) || null,
    [deleteId, driverRows]
  );

  const selectedDeleteName = selectedDeleteRow?.driverName?.trim() || "this driver";
  const deleteTitle = "Are you sure you want to delete this driver?";
  const deleteDescription = `Deleting "${selectedDeleteName}" will permanently remove this driver from your records. This action cannot be undone.`;

  const closeDeletePopup = () => {
    if (deleteDriverLoading) return;
    setDeletePopup(false);
    setDeleteId(null);
  };

  const closeStatusPopup = () => {
    if (updateDriverStatusLoading) return;
    setStatusPopup(DEFAULT_STATUS_POPUP_STATE);
  };

  const getDriversTablePayload = () => {
    const payload = {
      page: currentPage,
      limit,
    };

    if (searchText) {
      payload.search_text = searchText;
    }

    if (searchFilters) {
      payload.searchFilters = searchFilters;
    }

    return payload;
  };

  const refreshDriverTable = async () => {
    setTableLoading(true);

    try {
      await Promise.all([
        dispatch(fetchDriversPaginated(getDriversTablePayload())).unwrap(),
        dispatch(fetchDriverOverview()).unwrap(),
      ]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleStatusClick = (event, row) => {
    event.stopPropagation();

    if (!canChangeDriverStatus) {
      setNoStatusPermissionModal(true);
      return;
    }

    const triggerRect = event.currentTarget.getBoundingClientRect();
    const estimatedPopupHeight = 110;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const placement =
      spaceBelow < estimatedPopupHeight && spaceAbove > spaceBelow
        ? "top"
        : "bottom";

    setStatusPopup((previous) => {
      if (previous.open && String(previous.rowId) === String(row.id)) {
        return DEFAULT_STATUS_POPUP_STATE;
      }

      return {
        open: true,
        rowId: row.id,
        placement,
        triggerRect: {
          top: triggerRect.top,
          bottom: triggerRect.bottom,
          left: triggerRect.left,
        },
      };
    });
  };

  const handleStatusSelect = async (status, row) => {
    if (!canChangeDriverStatus) {
      closeStatusPopup();
      setNoStatusPermissionModal(true);
      return;
    }

    const userId = Number(row?.userId);
    if (!Number.isFinite(userId) || userId <= 0) {
      errorNotify("Invalid driver user id");
      return;
    }

    try {
      closeStatusPopup();
      await dispatch(
        updateDriverStatus({
          user_id: userId,
          status,
        })
      ).unwrap();
      await refreshDriverTable();
      successNotify("Driver status updated successfully");
    } catch (error) {
      errorNotify(error || "Failed to update driver status");
    }
  };

  const totalDrivers = getMetricOrFallback(
    driverOverviewData?.total_drivers,
    getMetricOrFallback(
      getDriversPaginatedData?.meta?.totalItems,
      getMetricOrFallback(getDriversPaginatedData?.total, driverRows.length)
    )
  );
  const activeDrivers = getMetricOrFallback(
    driverOverviewData?.active_drivers?.count,
    driverRows.filter((driver) => driver.status === "Active").length
  );
  const activeDriversPercentage = getNumberMetric(
    driverOverviewData?.active_drivers?.percentage,
    0
  );
  const onLeaveDrivers = getMetricOrFallback(
    driverOverviewData?.on_leave_drivers?.count,
    driverRows.filter((driver) => driver.status === "On Leave").length
  );
  const pendingLeaveApprovals = getNumberMetric(
    driverOverviewData?.on_leave_drivers?.pending_leave_approvals,
    0
  );
  const addedThisMonth = getNumberMetric(driverOverviewData?.added_this_month, 0);
  const criticalDriversFallback = driverRows.filter(
    (driver) => Number(driver.safetyScore ?? 0) < 60
  ).length;
  const criticalDrivers = getMetricOrFallback(
    driverOverviewData?.critical_drivers?.count ?? driverOverviewData?.critical_drivers,
    criticalDriversFallback
  );

  const statsData = [
    {
      title: "Total Drivers",
      value: totalDrivers,
      text: <span>{addedThisMonth} this month</span>,
      icon: partValueIcon,
    },
    {
      title: "Active",
      value: activeDrivers,
      className: "completed",
      text: <span>{activeDriversPercentage}% of total</span>,
      icon: partUtilizedIcon,
    },
    {
      title: "On Leave",
      value: onLeaveDrivers,
      className: "late",
      text: <span>{pendingLeaveApprovals} pending approvals</span>,
      icon: resolvedIssueIcon,
    },
    {
      title: "Critical Drivers",
      value: criticalDrivers,
      className: "pending-delivery",
      text: <span>Driver score below 60</span>,
      icon: lowStockIcon,
    },
  ];

  const StatusPopup = ({ row }) => {
    const popupPlacement = statusPopup.placement === "top" ? "top" : "bottom";
    const rect = statusPopup.triggerRect;
    const popupPositionStyle = rect
      ? popupPlacement === "top"
        ? { bottom: `${window.innerHeight - rect.top + 8}px`, left: `${rect.left}px` }
        : { top: `${rect.bottom + 8}px`, left: `${rect.left}px` }
      : { top: "calc(100% + 8px)", left: 0 };
    const availableOptions = getStatusUpdateOptionsForRow(row?.status);

    return (
      <div
        ref={statusPopupRef}
        style={{
          position: "fixed",
          minWidth: "158px",
          background: "#fff",
          border: "1px solid #EAECF0",
          borderRadius: "12px",
          boxShadow: "0px 10px 30px rgba(16, 24, 40, 0.12)",
          zIndex: 9999,
          padding: "6px",
          ...popupPositionStyle,
        }}
      >
        {availableOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleStatusSelect(option.value, row);
            }}
            disabled={updateDriverStatusLoading}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 10px",
              borderRadius: "8px",
              color: "#344054",
              cursor: updateDriverStatusLoading ? "not-allowed" : "pointer",
              opacity: updateDriverStatusLoading ? 0.6 : 1,
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {renderStatusIcon(option.icon)}
            {option.label}
          </button>
        ))}
      </div>
    );
  };

  const columns = [
    {
      name: "Driver ID",
      key: "driver_id",
      selector: (row) => (
        <span
          className="hyperlink"
          role="button"
          tabIndex={0}
          onClick={() => handleOpenDriverDetails(row)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenDriverDetails(row);
            }
          }}
        >
          {row.driverId || "-"}
        </span>
      ),
      tableColumn: false,
      filterName: "driverIdFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Driver Name",
      key: "driver_name",
      selector: (row) => (
        <div className="asset_driver_cell">
          <UserAvatar username={row.driverName} image={row.image} marginRight="0px" />
          <span className="asset_driver_name">{row.driverName || "-"}</span>
        </div>
      ),
      tableColumn: false,
      filterName: "driverNameFilter",
      type: "text",
      minWidth: "220px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => {
        const statusMeta = getStatusMeta(row.status);
        return (
          <div style={{ position: "relative", display: "inline-block" }}>
            <button
              type="button"
              className="asset_status_cell"
              onClick={(event) => handleStatusClick(event, row)}
              disabled={!canUpdateDriver || updateDriverStatusLoading}
              style={{
                color: statusMeta.color,
                border: "none",
                background: statusMeta.background || "transparent",
                borderRadius: "999px",
                cursor:
                  canUpdateDriver && !updateDriverStatusLoading
                    ? "pointer"
                    : "default",
                padding: "4px 10px 4px 6px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {renderStatusIcon(statusMeta.icon)}
              {row.status}
            </button>
            {statusPopup.open &&
              String(statusPopup.rowId) === String(row.id) && (
                <StatusPopup row={row} />
              )}
          </div>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "text",
      options: STATUS_OPTIONS,
      minWidth: "160px",
      allowOverflow: true,
      ignoreRowClick: true,
    },
    {
      name: "Employee Number",
      key: "employee_number",
      selector: (row) => (
        <span className="hyperlink" role="button" tabIndex={-1}>
          {row.employeeNumber || "-"}
        </span>
      ),
      tableColumn: false,
      filterName: "employeeNumberFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Contact Number",
      key: "contact_number",
      selector: (row) => row.contactNumber || "-",
      tableColumn: false,
      filterName: "contactNumberFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Supervisor",
      key: "supervisor",
      selector: (row) => row.supervisorName || "-",
      tableColumn: true,
      filterName: "supervisorIdFilter",
      type: "select",
      isMulti: true,
      filterComponent: SupervisorFilterDropdown,
      options: supervisorDropdownOptions,
      minWidth: "180px",
    },
    {
      name: "Driver Score",
      key: "safety_score",
      selector: (row) => {
        const scoreMeta = getScoreMeta(Number(row.safetyScore ?? 0));
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "42px",
              height: "38px",
              padding: "0 12px",
              borderRadius: "999px",
              background: scoreMeta.background,
              color: scoreMeta.color,
              fontWeight: 600,
            }}
          >
            {Number(row.safetyScore ?? 0)}
          </span>
        );
      },
      tableColumn: false,
      filterName: "safetyScoreFilter",
      type: "number",
      minWidth: "140px",
      omit: true,
    },
    {
      name: "Last Activity",
      key: "updated_at",
      selector: (row) => row.lastActivity,
      tableColumn: false,
      filterName: "lastActivityFilter",
      type: "date",
      minWidth: "150px",
    },
    {
      name: "Assigned Trips",
      key: "assigned_trips",
      selector: (row) => row.assignedTrips,
      tableColumn: false,
      filterName: "assignedTripsFilter",
      type: "number",
      minWidth: "140px",
    },
    {
      name: "Action",
      key: "action",
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateDriver && (
            <button
              type="button"
              className="edit_btn"
              title="Edit Driver"
              onClick={() => handleOpenDriverDetails(row)}
            >
              <LuPencil />
            </button>
          )}
          {canDeleteDriver && (
            <button
              type="button"
              className="delete_btn"
              title="Delete Driver"
              onClick={() => {
                setDeleteId(row.id);
                setDeletePopup(true);
              }}
            >
              <RiDeleteBinLine />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  const handleDeleteDriver = async () => {
    if (deleteId === null || deleteId === undefined || deleteDriverLoading) return;
    if (!canDeleteDriver) {
      errorNotify("You do not have permission to delete drivers.");
      return;
    }

    const targetId = Number(deleteId);
    if (!Number.isFinite(targetId)) {
      errorNotify("Invalid driver id");
      return;
    }

    try {
      await dispatch(deleteDriver(targetId)).unwrap();
      successNotify("Driver deleted successfully");
      closeDeletePopup();

      if (driverRows.length === 1 && currentPage > 1) {
        setCurrentPage((previous) => Math.max(1, previous - 1));
        return;
      }

      await refreshDriverTable();
    } catch (error) {
      errorNotify(error || "Failed to delete driver");
    }
  };

  return (
    <div>
      <Breadcrumbs values={["Operations", "Driver"]} />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDeleteDriver}
        loading={deleteDriverLoading}
        title={deleteTitle}
        description={deleteDescription}
      />

      <CustomModal
        show={noStatusPermissionModal}
        handleClose={() => setNoStatusPermissionModal(false)}
        heading="Access Restricted"
        size="sm"
      >
        <div style={{ padding: "24px 20px 8px", textAlign: "center" }}>
          <div style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 20px", boxShadow: "0 4px 12px rgba(239,68,68,0.2)",
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1C8.676 1 6 3.676 6 7v1H4v15h16V8h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" fill="#DC2626"/>
            </svg>
          </div>
          <h5 style={{ fontWeight: "700", color: "#111827", marginBottom: "10px", fontSize: "18px" }}>
            Access Restricted
          </h5>
          <p style={{ color: "#6B7280", fontSize: "14px", lineHeight: "1.6", margin: "0 0 24px" }}>
            You don't have permission to change the driver's status.
            Please contact your administrator to get access.
          </p>
          <button
            type="button"
            onClick={() => setNoStatusPermissionModal(false)}
            style={{
              background: "#DC2626", color: "#fff", border: "none",
              borderRadius: "8px", padding: "10px 32px", fontSize: "14px",
              fontWeight: "600", cursor: "pointer", width: "100%",
              marginBottom: "8px",
            }}
          >
            Okay, Got it
          </button>
        </div>
      </CustomModal>

      <CustomModal
        show={attendanceModalOpen}
        handleClose={closeAttendanceModal}
        heading="Mark Driver Attendance"
        size="lg"
        dialogClassName="driver_attendance_modal_dialog"
      >
        <div className="driver_attendance_modal">
          <div className="driver_attendance_date">
            <Input
              label="Attendance Date"
              placeholder="Select date"
              type="date"
              name="attendanceDate"
              value={attendanceDate}
              onChange={(event) => setAttendanceDate(event.target.value)}
              required
              maxValue={getTodayInputDate()}
            />
          </div>

          <div className="driver_attendance_table">
            <table>
              <thead>
                <tr>
                  <th>Driver ID</th>
                  <th>Driver Name</th>
                  <th>Employee Number</th>
                  <th>Contact Number</th>
                  <th>Attendance Status</th>
                  <th>Leave Reason</th>
                  <th style={{ color: "#1450c8" }}>Food</th>
                  <th style={{ color: "#1450c8" }}>TA/DA</th>
                  <th style={{ color: "#1450c8" }}>Trip</th>
                  <th style={{ color: "#1450c8" }}>Mobile</th>
                  <th style={{ color: "#1450c8" }}>Arrears</th>
                </tr>
              </thead>
              <tbody>
                {attendanceLoading ? (
                  <tr>
                    <td colSpan={11} className="text-center" style={{ padding: "24px", color: "#64748b" }}>
                      Loading drivers list...
                    </td>
                  </tr>
                ) : attendanceRows.length > 0 ? (
                  attendanceRows.map((row, index) => {
                    const leaveSelected = row.attendanceStatus === "Leave";
                    const statusStyles = getAttendanceStatusStyles(row.attendanceStatus);
                    const isSavedUnchanged = row.isAlreadyMarked &&
                      row.attendanceStatus === row.savedAttendanceStatus &&
                      (row.leaveReason || "") === (row.savedLeaveReason || "");

                    return (
                      <tr key={`${row.id || row.driverId}-${index}`}>
                        <td>{row.driverId || "-"}</td>
                        <td>
                          <div className="asset_driver_cell">
                            <UserAvatar
                              username={row.driverName}
                              image={row.image}
                              size={24}
                              fontSize="10px"
                              marginRight="0px"
                            />
                            <span className="asset_driver_name">{row.driverName || "-"}</span>
                          </div>
                        </td>
                        <td>{row.employeeNumber || "-"}</td>
                        <td>{row.contactNumber || "-"}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{
                              position: "relative",
                              flex: 1,
                              minWidth: "130px",
                            }}>
                                {isSavedUnchanged ? (
                                <span style={{
                                  position: "absolute",
                                  top: "-8px",
                                  left: "6px",
                                  background: "#ECFDF5",
                                  color: "#059669",
                                  border: "1px solid #A7F3D0",
                                  borderRadius: "6px",
                                  padding: "0px 6px",
                                  fontSize: "9px",
                                  fontWeight: "700",
                                  lineHeight: "16px",
                                  zIndex: 1,
                                  letterSpacing: "0.3px",
                                  pointerEvents: "none",
                                }}>
                                  ✓ Saved
                                </span>
                              ) : !row.isAlreadyMarked ? (
                                <span style={{
                                  position: "absolute",
                                  top: "-8px",
                                  left: "6px",
                                  background: "#FFF7ED",
                                  color: "#C2410C",
                                  border: "1px solid #FED7AA",
                                  borderRadius: "6px",
                                  padding: "0px 6px",
                                  fontSize: "9px",
                                  fontWeight: "700",
                                  lineHeight: "16px",
                                  zIndex: 1,
                                  letterSpacing: "0.3px",
                                  pointerEvents: "none",
                                }}>
                                  Not Marked
                                </span>
                              ) : null}
                              <Select
                                label=""
                                placeholder="Select Status"
                                className="attendance-status-select-wrapper"
                                options={ATTENDANCE_STATUS_OPTIONS}
                                value={
                                  ATTENDANCE_STATUS_OPTIONS.find(
                                    (option) => option.value === row.attendanceStatus
                                  ) || null
                                }
                                name={`attendanceStatus-${row.id}`}
                                onChange={(option) =>
                                  updateAttendanceRow(
                                    row.id,
                                    "attendanceStatus",
                                    option ? option.value : "Present"
                                  )
                                }
                                isSearch={false}
                                required={false}
                                menuPortalTarget={document.body}
                                customStyles={{
                                  control: (baseStyles) => ({
                                    ...baseStyles,
                                    background: row.isAlreadyMarked ? statusStyles.background : "#FAFAFA",
                                    border: isSavedUnchanged
                                      ? `2px solid ${statusStyles.border.replace("1px solid ", "")}`
                                      : row.isAlreadyMarked
                                      ? statusStyles.border
                                      : "1px dashed #D1D5DB",
                                    borderRadius: "6px",
                                    padding: "2px 8px",
                                    minHeight: "32px",
                                    cursor: "pointer",
                                    pointerEvents: "auto",
                                    opacity: 1,
                                    boxShadow: "none",
                                    "&:hover": { border: isSavedUnchanged ? `2px solid ${statusStyles.border.replace("1px solid ", "")}` : row.isAlreadyMarked ? statusStyles.border : "1px dashed #9CA3AF" },
                                  }),
                                  singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    color: row.isAlreadyMarked ? statusStyles.color : "#6B7280",
                                    fontWeight: 600,
                                  }),
                                  dropdownIndicator: (baseStyles) => ({
                                    ...baseStyles,
                                    color: row.isAlreadyMarked ? statusStyles.color : "#9CA3AF",
                                  }),
                                }}
                              />
                            </div>
                            {row.isAlreadyMarked && (
                              <button
                                type="button"
                                onClick={() => handleUnmarkAttendance(row)}
                                style={{
                                  background: "#FEF2F2",
                                  color: "#DC2626",
                                  border: "1px solid #FCA5A5",
                                  borderRadius: "6px",
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              >
                                Unmark
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <Input
                            label=""
                            placeholder="Enter leave reason"
                            type="text"
                            name={`leaveReason-${row.id}`}
                            value={leaveSelected ? row.leaveReason : ""}
                            onChange={(event) =>
                              updateAttendanceRow(row.id, "leaveReason", event.target.value)
                            }
                            disabled={!leaveSelected}
                            className={attendanceErrors[row.id] ? "driver_leave_reason_error" : ""}
                            required={leaveSelected}
                          />
                        </td>
                        {["food", "tada", "trip", "mobile", "arrears"].map((aType) => {
                          const driverAllowances = allowancesByDriver[row.id] || [];
                          const allowance = driverAllowances.find((a) => a.allowance_type === aType);
                          return (
                            <td key={aType} style={{ textAlign: "center" }}>
                              {allowance ? (() => {
                                const isMarked = !!(allowanceEntriesMap[row.id]?.[allowance.id]?.is_marked);
                                return (
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                                    <input
                                      type="checkbox"
                                      checked={isMarked}
                                      onChange={(e) => toggleAllowanceEntry(row.id, allowance.id, e.target.checked)}
                                      style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#1450c8" }}
                                    />
                                    {isMarked && (
                                      <span style={{ fontSize: "11px", color: "#1450c8", fontWeight: 600, whiteSpace: "nowrap" }}>
                                        PKR {Number(allowance.amount || 0).toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                );
                              })() : (
                                <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="text-center" style={{ padding: "24px", color: "#64748b" }}>
                      No drivers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="driver_attendance_modal_actions">
            <Button
              cancelBtn={closeAttendanceModal}
              submitBtn={saveAttendance}
              submitText="Save Attendance & Allowances"
              type="button"
              shouldRow={false}
              loading={saveAttendanceLoading}
              cancelBtnDisabled={saveAttendanceLoading}
            />
          </div>
        </div>
      </CustomModal>

      <div className="access_view">
        <h2>Driver</h2>

        <div className="services_buttons">
          <ExportButton
            type="pdf"
            onClick={handleExportPdf}
            disabled={!hasActiveFilters || pdfExportLoading}
            loading={pdfExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          <ExportButton
            type="excel"
            onClick={handleExportExcel}
            disabled={!hasActiveFilters || excelExportLoading}
            loading={excelExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          <Dropdown className="attendance_dropdown">
            <Dropdown.Toggle
              variant="outline-secondary"
              id="driver-attendance-dropdown"
              className="attendance_dropdown_toggle"
            >
              Attendance <LuChevronDown className="ms-1" size={14} />
            </Dropdown.Toggle>

            <Dropdown.Menu className="attendance_dropdown_menu shadow-sm">
              <Dropdown.Item
                onClick={openAttendanceModal}
                className="d-flex align-items-center gap-2 py-2"
              >
                <LuCalendar size={16} color="#4014B8" />
                <span>Mark Attendance</span>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => navigate("/driver-safety/attendance-logs")}
                className="d-flex align-items-center gap-2 py-2"
              >
                <LuClock size={16} color="#4014B8" />
                <span>Attendance Logs</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {canCreateDriver && (
            <button type="button" onClick={() => navigate("/driver-safety/add")}>
              {plusIcon} Add Driver
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} />

      <NewTableComponent
        tableClassName="parts_table_fix driver_status_table"
        showAddBtn={false}
        columns={columns}
        data={driverRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={(nextLimit) => {
          setLimit(nextLimit);
          setCurrentPage(1);
        }}
        totalItems={totalDrivers}
        totalPages={Number(getDriversPaginatedData?.meta?.totalPages || 1)}
        onSearch={(value) => {
          setSearchText(value);
          setCurrentPage(1);
          closeStatusPopup();
        }}
        loading={getDriversPaginatedLoading || tableLoading}
        paginationLoading={false}
        setSearchFilters={(filters) => {
          setSearchFilters(filters);
          setCurrentPage(1);
          closeStatusPopup();
        }}
        searchFilters={searchFilters}
        limitOptions={[10, 20, 50, 100]}
      />
    </div>
  );
};

export default DriverSafety;
